// Hook de acceso a API de Reemplazos 2.0 reutilizando servicios existentes
// No cambia endpoints ni contratos; solo normaliza errores y tipa respuestas

import OperariosRemplazos from 'src/services/operarioRemplazo';
import Operarios from 'src/services/operarios';

export interface ApiResult<T> {
  ok: boolean;
  data?: T;
  error?: string;
}

export interface AsignacionItem {
  id: number;
  NombrePServicio: string;
  [k: string]: any;
}

// Respuesta del endpoint /punto_servicio/cupo_por_dia.
// Nota: en modo normal (teórico) solo viene {fecha, dia, cupo_total}.
// En modo real_hp el backend puede anexar desglose para QA/UX:
// - cupo_teorico: suma teórica de asignaciones del ausente
// - reemplazos_usados: consumo por reemplazos ya cargados
// - procesadas_usadas: consumo por HorasProcesadas (PROCESAD*) del ausente
export interface CupoPorDiaItem {
  fecha: string;
  dia: string;
  cupo_total: string;
  cupo_teorico?: string;
  reemplazos_usados?: string;
  procesadas_usadas?: string;
  cedula_ausente?: string;
  [k: string]: any;
}

export function useReemplazosApi() {
  // listar operarios y supervisores: retorna array plano "Nombre - LEG"
  // Se usa el endpoint /usuario/operarios-supervisores para incluir cargos SUPERVISOR
  // (necesarios como reemplazados/reemplazantes) ademas de OPERARIO.
  const listarOperarios = async (): Promise<ApiResult<string[]>> => {
    try {
      const lista = await Operarios.listarOperariosSupervisores();
      const datos = Array.isArray(lista)
        ? lista.map((o: any) => `${o.nombre} - ${o.legajo}`)
        : [];
      return { ok: true, data: datos };
    } catch (e: any) {
      return { ok: false, error: 'No se pudo obtener la lista de operarios' };
    }
  };

  // asignaciones por rango para operario reemplazado
  const listarAsignaciones = async (payload: any): Promise<ApiResult<AsignacionItem[]>> => {
    try {
      const res = await OperariosRemplazos.listarOperariosRemplazo(payload) as any;
      const datos = Array.isArray(res?.data) ? res.data : [];
      return { ok: true, data: datos };
    } catch (e: any) {
      if (e?.response?.status === 404) return { ok: true, data: [] };
      const msg = e?.response?.data?.error || 'Error al listar asignaciones';
      return { ok: false, error: msg };
    }
  };

  // cupos disponibles por asignaciones y rango
  const calcularCupos = async (payload: any): Promise<ApiResult<any>> => {
    try {
      const res = await OperariosRemplazos.listarCupo(payload);
      return { ok: true, data: res?.data };
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'Error al calcular cupos';
      return { ok: false, error: msg };
    }
  };

  // cupo por fecha (para validar max diario)
  const cupoPorDia = async (payload: any): Promise<ApiResult<CupoPorDiaItem[]>> => {
    try {
      const res = await (OperariosRemplazos as any).cupoPorDia?.(payload);
      const datos = res?.data?.dias || [];
      return { ok: true, data: datos };
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'Error al obtener cupo por día';
      return { ok: false, error: msg };
    }
  };

  // calcular cupo real del rango sumando remanente diario (usa cupo_por_dia)
  const calcularCuposReal = async (payload: any): Promise<ApiResult<{ NORMA: string; TOTAL: string }>> => {
    try {
      const res = await (OperariosRemplazos as any).cupoPorDia?.(payload);
      const dias = res?.data?.dias || [];
      const toMin = (hhmm: string) => {
        try { const [h, m] = (hhmm || '0:00').split(':').map((n: any) => parseInt(n, 10) || 0); return (h * 60) + m; } catch { return 0; }
      };
      const totalMin = dias.reduce((acc: number, d: any) => acc + toMin(d?.cupo_total || '0:00'), 0);
      const hh = String(Math.floor(totalMin / 60)).padStart(2, '0');
      const mm = String(totalMin % 60).padStart(2, '0');
      const hhmm = `${hh}:${mm}`;
      return { ok: true, data: { NORMA: hhmm, TOTAL: hhmm } };
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'Error al calcular cupo real';
      return { ok: false, error: msg };
    }
  };

  // validar solape
  const validarSolape = async (payload: any): Promise<ApiResult<null>> => {
    try {
      const res = await OperariosRemplazos.operarioDisponible(payload);
      if (res?.status === 200) return { ok: true };
      return { ok: false, error: 'Error al validar solapes' };
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'El reemplazante posee asignaciones solapadas';
      return { ok: false, error: msg };
    }
  };

  // verificar duplicado exacto en backend
  const existeDuplicado = async (payload: any): Promise<ApiResult<{ exists: boolean; id?: number }>> => {
    try {
      const res = await (OperariosRemplazos as any).existeDuplicado?.(payload);
      if (!res) throw new Error('Endpoint no disponible');
      const data = res?.data || {};
      return { ok: true, data: { exists: !!data.exists, id: data.id } };
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'Error al verificar duplicado';
      return { ok: false, error: msg };
    }
  };

  // verificar día libre por fecha en el rango y días seleccionados
  const verificarDiaLibre = async (payload: any): Promise<ApiResult<{ anyLibre: boolean; fechas: Array<{ fecha: string; dia: string; libre: boolean }> }>> => {
    try {
      const res = await (OperariosRemplazos as any).diaLibre?.(payload);
      if (!res) throw new Error('Endpoint no disponible');
      const data = res?.data || {};
      return { ok: true, data: { anyLibre: !!data.anyLibre, fechas: data.fechas || [] } };
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'Error al verificar día libre';
      return { ok: false, error: msg };
    }
  };

  // días disponibles según rango
  const obtenerDiasDisponibles = async (payload: any): Promise<ApiResult<Array<{ dia: string|null; nombre: string }>>> => {
    try {
      const res = await OperariosRemplazos.checkDias(payload);
      return { ok: true, data: res?.data?.dias || [] };
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'Error al obtener días disponibles';
      return { ok: false, error: msg };
    }
  };

  // listar reemplazos existentes (vista del backend)
  const listarReemplazosExistentes = async (payload: any): Promise<ApiResult<any[]>> => {
    try {
      const res = await OperariosRemplazos.vistaRemplazo(payload as any);
      const datos = res?.data?.datos || [];
      return { ok: true, data: datos };
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'Error al listar reemplazos existentes';
      return { ok: false, error: msg };
    }
  };

  // crear reemplazo (batch)
  const crearRemplazo = async (payload: any[]): Promise<ApiResult<string>> => {
    try {
      const res = await OperariosRemplazos.cargarRemplazo(payload as any);
      const info = res?.data?.info || 'Reemplazo creado';
      return { ok: true, data: info };
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'No se pudo crear el reemplazo';
      return { ok: false, error: msg };
    }
  };

  // diagnostico de validaciones en backend
  const diagnosticoRemplazo = async (payload: any): Promise<ApiResult<any>> => {
    try {
      const res = await (OperariosRemplazos as any).diagnosticoRemplazo?.(payload);
      return { ok: true, data: res?.data || {} };
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || 'No se pudo diagnosticar el reemplazo';
      return { ok: false, error: msg };
    }
  };

  // eliminar reemplazo existente
  const eliminarReemplazo = async (payload: { id: number }): Promise<ApiResult<null>> => {
    // El servicio no lanza: devuelve response.data o { error } (con el error de axios en 400).
    // Ej. de rechazo: tiene horas procesadas u otro reemplazo heredó su cupo (TD-11585).
    try {
      const res: any = await OperariosRemplazos.EliminarVistaRemplazo(payload as any);
      if (!res?.error) return { ok: true };
      const msg = typeof res.error === 'string' ? res.error : res.error?.response?.data?.error;
      return { ok: false, error: msg || 'No se pudo eliminar el reemplazo' };
    } catch {
      return { ok: false, error: 'No se pudo eliminar el reemplazo' };
    }
  };

  // --- NUEVO: API de Compensación (stubs compatibles con backend) ---
  // saldo de origen por día en el rango
  const compensacionSaldoOrigen = async (
    payload: any
  ): Promise<ApiResult<Array<{ fecha: string; dia: string; saldo: string }>>> => {
    try {
      const res = await (OperariosRemplazos as any).compensacionSaldoOrigen?.(payload);
      const lista = res?.data?.dias || [];
      return { ok: true, data: lista };
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'Error al obtener saldo de origen';
      return { ok: false, error: msg };
    }
  };

  // reservar compensación (devuelve cupo virtual por fecha)
  const compensacionReservar = async (
    payload: any
  ): Promise<ApiResult<{ idReserva?: any; cupoVirtualPorFecha?: Record<string, string> }>> => {
    try {
      const res = await (OperariosRemplazos as any).compensacionReservar?.(payload);
      return { ok: true, data: res?.data || {} };
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'No se pudo reservar la compensación';
      return { ok: false, error: msg };
    }
  };

  // cancelar reserva de compensación
  const compensacionCancelar = async (payload: any): Promise<ApiResult<null>> => {
    try {
      const res = await (OperariosRemplazos as any).compensacionCancelar?.(payload);
      if (res?.status === 200) return { ok: true };
      return { ok: true };
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'No se pudo cancelar la compensación';
      return { ok: false, error: msg };
    }
  };

  return { listarOperarios, listarAsignaciones, calcularCupos, cupoPorDia, calcularCuposReal, validarSolape, existeDuplicado, verificarDiaLibre, obtenerDiasDisponibles, listarReemplazosExistentes, crearRemplazo, eliminarReemplazo, diagnosticoRemplazo, compensacionSaldoOrigen, compensacionReservar, compensacionCancelar };
}


